import {
    Injector,
    NgModule,
    Renderer2,
    RendererStyleFlags2,
    RendererType2,
} from '@angular/core';
import {
    BrowserModule,
    EventManager,
    ɵDomRendererFactory2,
    ɵDomSharedStylesHost,
} from '@angular/platform-browser';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import componentTypes from './rvirus.cmap';
import iocTypes from './rvirus.imap';
import { IocContext } from './rvirus.ioc';
import store from './store';

const PREFIX = 'rvirus';

enum RVirusNodeState {
    NONE,
    RENDER,
    DESTROY,
}

function encapsulateInRedux(type: React.ReactType, props: any): JSX.Element {
    return (
        <Provider store={store}>{React.createElement(type, props)}</Provider>
    );
}

class RVirusNode {
    private static readonly TAG = RVirusNode.name;

    private _container: HTMLElement;
    props: { [key: string]: any };
    private _state: RVirusNodeState;

    // children: Array<RVirusNode>;

    get isPendingRender(): boolean {
        return this._state === RVirusNodeState.RENDER;
    }

    get isPendingDestruction(): boolean {
        return this._state === RVirusNodeState.DESTROY;
    }

    // eslint-disable-next-line accessor-pairs
    set container(value: HTMLElement) {
        this._container = value;
    }

    // eslint-disable-next-line accessor-pairs
    set state(value: RVirusNodeState) {
        this._state = value;
    }

    constructor(public typeName: string) {
        this._state = RVirusNodeState.NONE;
        this.props = {};

        // this.children = [];
    }

    // appendChild(node: RVirusNode): void {
    //     this.children.push(node);
    // }

    setProperty(key: string, value: any): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusNode.TAG, 'setProperty', key, value);
        this.props[key] = value;
        this._state = RVirusNodeState.RENDER;
    }

    render(): React.ReactElement<any> {
        CMPS_APP_DEF.ENV !== 'prod' && console.log(RVirusNode.TAG, 'render');
        if (this._state !== RVirusNodeState.RENDER) return;
        this._state = RVirusNodeState.NONE;
        const type = componentTypes[this.typeName];
        // const children = this.children.map(node => node.render());
        const el = encapsulateInRedux(type, { ...this.props });
        ReactDOM.render(el, this._container);
        return el;
    }

    destroy(): void {
        CMPS_APP_DEF.ENV !== 'prod' && console.log(RVirusNode.TAG, 'destroy');
        if (this._state !== RVirusNodeState.DESTROY) return;
        this._state = RVirusNodeState.NONE;
        const container =
            this._container instanceof RVirusNode
                ? this._container._container
                : this._container;
        ReactDOM.unmountComponentAtNode(container);
        this._container = null;
    }
}

function RVirusNodeFactory(name: string): RVirusNode {
    return new RVirusNode(name);
}

export class RVirusRenderer2 implements Renderer2 {
    private static readonly TAG = RVirusRenderer2.name;

    rootNodes: RVirusNode[];
    readonly data: { [p: string]: any };

    constructor(private readonly _onDestroy: () => void) {
        this.rootNodes = [];
    }

    addClass(el: any, name: string): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'addClass', el, name);
    }

    appendChild(parent: HTMLElement | RVirusNode, newChild: RVirusNode): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'appendChild', parent, newChild);
        if (!newChild) return;
        newChild.container = parent as any;
        newChild.state = RVirusNodeState.RENDER;
        if (!(parent instanceof RVirusNode)) {
            this.rootNodes.push(newChild);
            // return;
        }
        // parent.appendChild(newChild);
    }

    createComment(value: string): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'createComment', value);
        // return RVirusNodeFactory('comment');
    }

    createElement(name: string, namespace?: string | null): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'createElement', name, namespace);
        return RVirusNodeFactory(name);
    }

    createText(value: string): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'createText', value);
        // return RVirusNodeFactory('text');
    }

    destroy(): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'destroy');
        const nodes = this.rootNodes.splice(0, this.rootNodes.length);
        nodes.forEach((node) => {
            node.state = RVirusNodeState.DESTROY;
            node.destroy();
        });
        this._onDestroy();
    }

    destroyNode = null;

    insertBefore(parent: any, newChild: RVirusNode, refChild: any): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'insertBefore',
                parent,
                newChild,
                refChild,
            );
        if (!parent || !refChild) return;
        const container = document.createElement('div');
        parent.insertBefore(container, refChild);
        newChild.container = container;
        newChild.state = RVirusNodeState.RENDER;
    }

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    listen(
        target: 'window' | 'document' | 'body' | RVirusNode,
        eventName: string,
        // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
        callback: (event: any) => boolean | void,
    ): () => void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'listen',
                target,
                eventName,
                callback,
            );
        if (target instanceof RVirusNode) {
            target.setProperty(eventName, callback);
        }
        return () => {
            //
        };
    }

    nextSibling(node: any): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'nextSibling', node);
    }

    parentNode(node: any): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'parentNode', node);
    }

    removeAttribute(el: any, name: string, namespace?: string | null): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'removeAttribute',
                el,
                name,
                namespace,
            );
    }

    removeChild(parent: HTMLElement | RVirusNode, oldChild: RVirusNode): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'removeChild', parent, oldChild);
        if (!oldChild) {
            return;
        }
        oldChild.state = RVirusNodeState.DESTROY;
        if (!(parent instanceof RVirusNode)) {
            try {
                this.rootNodes.splice(this.rootNodes.indexOf(oldChild), 1);
            } catch {}
            // return;
        }
        // parent.removeChild(oldChild);
    }

    removeClass(el: any, name: string): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'removeClass', el, name);
    }

    removeStyle(el: any, style: string, flags?: RendererStyleFlags2): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'removeStyle', el, style, flags);
    }

    selectRootElement(selectorOrNode: string | any): any {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'selectRootElement',
                selectorOrNode,
            );
    }

    setAttribute(
        el: RVirusNode,
        name: string,
        value: string,
        namespace?: string | null,
    ): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'setAttribute',
                el,
                name,
                value,
                namespace,
            );
        el.setProperty(name, value);
    }

    setProperty(el: RVirusNode, name: string, value: any): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'setProperty', el, name, value);
        el.setProperty(name, value);
    }

    setStyle(
        el: any,
        style: string,
        value: any,
        flags?: RendererStyleFlags2,
    ): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(
                RVirusRenderer2.TAG,
                'setStyle',
                el,
                style,
                value,
                flags,
            );
    }

    setValue(node: RVirusNode, value: string): void {
        CMPS_APP_DEF.ENV !== 'prod' &&
            console.log(RVirusRenderer2.TAG, 'setValue', node, value);
        node.setProperty('value', value);
    }
}

export class RVirusRendererFactory2 extends ɵDomRendererFactory2 {
    private static readonly TAG = RVirusRendererFactory2.name;

    private readonly _renderers: RVirusRenderer2[];

    constructor(
        eventManager: EventManager,
        sharedStylesHost: ɵDomSharedStylesHost,
    ) {
        super(eventManager, sharedStylesHost, 'cmps');
        this._renderers = [];
    }

    createRenderer(element: any, type: RendererType2 | null): Renderer2 {
        if (
            (element &&
                element instanceof Element &&
                (element.hasAttribute(PREFIX) ||
                    element.tagName.toLocaleLowerCase().startsWith(PREFIX))) ||
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (type &&
                type.styles &&
                type.styles.length !== 0 &&
                type.styles[0] === PREFIX)
        ) {
            CMPS_APP_DEF.ENV !== 'prod' &&
                console.log(
                    RVirusRendererFactory2.TAG,
                    'createRenderer',
                    element,
                    type,
                );
            const r = new RVirusRenderer2(() => {
                try {
                    this._renderers.splice(this._renderers.indexOf(r), 1);
                } catch {}
            });
            this._renderers.push(r);
            return r;
        }
        return super.createRenderer(element, type);
    }

    begin(): void {
        this._renderers.forEach((e) =>
            e.rootNodes
                .filter((node) => node.isPendingRender)
                .forEach((node) => node.render()),
        );
    }

    end(): void {
        this._renderers.forEach((e) =>
            e.rootNodes
                .filter((node) => node.isPendingDestruction)
                .forEach((node) => node.destroy()),
        );
    }
}

@NgModule({
    imports: [BrowserModule],
    exports: [BrowserModule],
    providers: [
        // ɵ => https://github.com/angular/angular/issues/21058#issuecomment-352894948
        {
            provide: ɵDomRendererFactory2,
            useClass: RVirusRendererFactory2,
            deps: [EventManager, ɵDomSharedStylesHost],
        },
    ],
})
export class RVirusBrowserModule {
    constructor(private readonly _injector: Injector) {
        Object.keys(iocTypes).forEach((t) => {
            IocContext.container
                .bind(iocTypes[t].identifier)
                .toDynamicValue(() => this._injector.get(iocTypes[t].class))
                .inSingletonScope();
        });
    }
}
